import React, { useState } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  AlertTriangle,
  XCircle,
  FileImage,
  HelpCircle,
  ChevronDown,
  ChevronUp,
  Lightbulb,
  ImageIcon,
  FileX,
  Maximize2,
} from 'lucide-react';
import { useImportTranslations } from '@/hooks/useImportTranslations';

interface BulkImageError {
  filename: string;
  sku: string;
  error: string;
  type: 'dimensions' | 'fileSize' | 'format' | 'corrupted' | 'noProduct' | 'general';
}

interface BulkImageErrorDisplayProps {
  errors: BulkImageError[];
  totalFiles: number;
  onRetry?: () => void;
  onDownloadReport?: () => void;
  className?: string;
}

const getErrorIcon = (type: BulkImageError['type']) => {
  switch (type) {
    case 'dimensions':
      return <Maximize2 className="size-4" />;
    case 'fileSize':
      return <AlertTriangle className="size-4" />;
    case 'format':
      return <FileX className="size-4" />;
    case 'corrupted':
      return <XCircle className="size-4" />;
    case 'noProduct':
      return <FileImage className="size-4" />;
    default:
      return <HelpCircle className="size-4" />;
  }
};

const getErrorColor = (type: BulkImageError['type']) => {
  switch (type) {
    case 'dimensions':
      return 'orange';
    case 'fileSize':
      return 'red';
    case 'format':
      return 'red';
    case 'corrupted':
      return 'red';
    case 'noProduct':
      return 'blue';
    default:
      return 'gray';
  }
};

export function BulkImageErrorDisplay({
  errors,
  totalFiles,
  onRetry,
  onDownloadReport,
  className = '',
}: BulkImageErrorDisplayProps) {
  const { getErrorMessage, getButtonText, t } = useImportTranslations();
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  const getCategoryTitle = (type: BulkImageError['type']) => {
    switch (type) {
      case 'dimensions':
        return t('imports.bulkImages.errorDisplay.categoryTitles.dimensions');
      case 'fileSize':
        return t('imports.bulkImages.errorDisplay.categoryTitles.fileSize');
      case 'format':
        return t('imports.bulkImages.errorDisplay.categoryTitles.format');
      case 'corrupted':
        return t('imports.bulkImages.errorDisplay.categoryTitles.corrupted');
      case 'noProduct':
        return t('imports.bulkImages.errorDisplay.categoryTitles.noProduct');
      default:
        return t('imports.bulkImages.errorDisplay.categoryTitles.general');
    }
  };

  const getCategoryDescription = (type: BulkImageError['type']) => {
    switch (type) {
      case 'dimensions':
        return t('imports.bulkImages.errorDisplay.categoryDescriptions.dimensions');
      case 'fileSize':
        return t('imports.bulkImages.errorDisplay.categoryDescriptions.fileSize');
      case 'format':
        return t('imports.bulkImages.errorDisplay.categoryDescriptions.format');
      case 'corrupted':
        return t('imports.bulkImages.errorDisplay.categoryDescriptions.corrupted');
      case 'noProduct':
        return t('imports.bulkImages.errorDisplay.categoryDescriptions.noProduct');
      default:
        return t('imports.bulkImages.errorDisplay.categoryDescriptions.general');
    }
  };

  const categorizeErrors = (errors: BulkImageError[]) => {
    const categories = {
      dimensions: {
        errors: [] as BulkImageError[],
        solutions: t('imports.bulkImages.errorDisplay.solutionCategories.dimensions.items', {
          returnObjects: true,
        }) as unknown as string[],
      },
      fileSize: {
        errors: [] as BulkImageError[],
        solutions: t('imports.bulkImages.errorDisplay.solutionCategories.fileSize.items', {
          returnObjects: true,
        }) as unknown as string[],
      },
      format: {
        errors: [] as BulkImageError[],
        solutions: t('imports.bulkImages.errorDisplay.solutionCategories.format.items', {
          returnObjects: true,
        }) as unknown as string[],
      },
      corrupted: {
        errors: [] as BulkImageError[],
        solutions: t('imports.bulkImages.errorDisplay.solutionCategories.corrupted.items', {
          returnObjects: true,
        }) as unknown as string[],
      },
      noProduct: {
        errors: [] as BulkImageError[],
        solutions: t('imports.bulkImages.errorDisplay.solutionCategories.noProduct.items', {
          returnObjects: true,
        }) as unknown as string[],
      },
      general: {
        errors: [] as BulkImageError[],
        solutions: t('imports.bulkImages.errorDisplay.solutionCategories.general.items', {
          returnObjects: true,
        }) as unknown as string[],
      },
    };

    errors.forEach(error => {
      categories[error.type].errors.push(error);
    });

    return Object.entries(categories)
      .filter(([_, category]) => category.errors.length > 0)
      .map(([type, category]) => ({
        type: type as BulkImageError['type'],
        ...category,
      }));
  };

  const categorizedErrors = categorizeErrors(errors);
  const successfulFiles = totalFiles - errors.length;

  const toggleCategory = (type: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(type)) {
        newSet.delete(type);
      } else {
        newSet.add(type);
      }
      return newSet;
    });
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Summary Card */}
      <Card className="border-l-4 border-l-red-500">
        <CardHeader>
          <div className="flex items-center space-x-2">
            <XCircle className="size-5 text-red-500" />
            <CardTitle className="text-lg">{t('imports.bulkImages.errorDisplay.title')}</CardTitle>
          </div>
          <CardDescription>
            {t('imports.bulkImages.errorDisplay.description', {
              failed: errors.length,
              total: totalFiles,
            })}
            {successfulFiles > 0 && (
              <span className="ml-2 text-green-600">
                {t('imports.bulkImages.errorDisplay.successCount', {
                  count: successfulFiles,
                })}
              </span>
            )}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Action Buttons */}
          <div className="flex space-x-2">
            {onRetry && (
              <Button onClick={onRetry} variant="outline" size="sm">
                {getButtonText('retry')}
              </Button>
            )}
            {onDownloadReport && (
              <Button onClick={onDownloadReport} variant="outline" size="sm">
                {t('imports.bulkImages.errorDisplay.downloadErrorReport')}
              </Button>
            )}
          </div>

          {/* Error Categories */}
          <div className="space-y-3">
            {categorizedErrors.map(({ type, errors: categoryErrors, solutions }) => (
              <Collapsible
                key={type}
                open={expandedCategories.has(type)}
                onOpenChange={() => toggleCategory(type)}
              >
                <CollapsibleTrigger asChild>
                  <div className="flex cursor-pointer items-center justify-between rounded-lg bg-muted p-3 hover:bg-muted/80">
                    <div className="flex items-center space-x-3">
                      {getErrorIcon(type)}
                      <div>
                        <div className="font-medium">{getCategoryTitle(type)}</div>
                        <div className="text-sm text-muted-foreground">
                          {getCategoryDescription(type)}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Badge variant="destructive">
                        {t('imports.errorHandling.errorCount', {
                          count: categoryErrors.length,
                        })}
                      </Badge>
                      {expandedCategories.has(type) ? (
                        <ChevronUp className="size-4" />
                      ) : (
                        <ChevronDown className="size-4" />
                      )}
                    </div>
                  </div>
                </CollapsibleTrigger>

                <CollapsibleContent className="mt-3">
                  <div className="space-y-3">
                    {/* Solutions */}
                    <Alert>
                      <Lightbulb className="size-4" />
                      <AlertTitle>
                        {t('imports.bulkImages.errorDisplay.solutionCategories.dimensions.title')}
                      </AlertTitle>
                      <AlertDescription>
                        <ul className="mt-2 list-inside list-disc space-y-1">
                          {solutions.map((solution, index) => (
                            <li key={index} className="text-sm">
                              {solution}
                            </li>
                          ))}
                        </ul>
                      </AlertDescription>
                    </Alert>

                    {/* Error Details */}
                    <div className="rounded-lg border bg-background p-3">
                      <div className="mb-2 text-sm font-medium">
                        {t('imports.bulkImages.errorDisplay.affectedFiles')}
                      </div>
                      <div className="max-h-40 space-y-2 overflow-y-auto">
                        {categoryErrors.slice(0, 10).map((error, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between rounded bg-muted/50 p-2 text-sm"
                          >
                            <div className="flex items-center space-x-2">
                              <ImageIcon className="size-3 text-muted-foreground" />
                              <span className="font-mono">{error.filename}</span>
                              {error.sku && (
                                <Badge variant="outline" className="text-xs">
                                  {t('imports.errorHandling.sku')} {error.sku}
                                </Badge>
                              )}
                            </div>
                            <div className="max-w-xs truncate text-xs text-muted-foreground">
                              {error.error}
                            </div>
                          </div>
                        ))}
                        {categoryErrors.length > 10 && (
                          <div className="py-2 text-center text-xs text-muted-foreground">
                            {t('imports.bulkImages.errorDisplay.moreFiles', {
                              count: categoryErrors.length - 10,
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default BulkImageErrorDisplay;
